import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "react-bootstrap";
import { Context } from "../App";
import * as Constants from "../api/constants";
import AddonItemImage from "../components/AddonItemImage";
import { normalizeAddonMetaPayload } from "../utils/normalizeAddonMeta";
import {
  loadBookingFlow,
  saveBookingFlow,
  addonsArrayToMap,
  persistAddonMeta,
} from "../utils/bookingFlowStorage";
import { getAdminBookingPaths } from "../utils/adminBookingPaths";
import "../partyBossTheme.css";

function hasNonemptyAddonCategories(addonMetaData) {
  const raw = addonMetaData?.addonsData || {};
  return Object.keys(raw).some(
    (k) => Array.isArray(raw[k]) && raw[k].length > 0
  );
}

const PHOTOS =
  "Timing of the photography is according to the availability of the photographer. Only soft copies will be provided.";
const PHOTOPROPS =
  "For photoclippings, our team will reach out to you on the day of booking. You need to send 16 soft copies of the photos you want to place inside the theater. You can take those hard copies with you after the party!";

/**
 * Step 3: add-ons as full page with left category sidebar (no modal).
 */
export default function AddonsWizardPage() {
  const navigate = useNavigate();
  const { totalCost, addonMetaData, setAddonMetaData } = useContext(Context);
  const draft = loadBookingFlow();
  const adminPaths = getAdminBookingPaths();
  const fallbackFetchedRef = useRef(false);
  const baseTheatreCost =
    Number(totalCost) > 0
      ? Number(totalCost)
      : Number(draft?.theatreBaseCost) || 0;
  const totalAddons = useMemo(() => {
    const data = addonMetaData?.addonsData || {};
    const keys = Object.keys(data).filter(
      (k) => Array.isArray(data[k]) && data[k].length > 0
    );
    const d = loadBookingFlow();
    const noDeco =
      d?.wantsDecoration === "No" ||
      d?.wantsDecoration === "NO" ||
      d?.wantsDecoration === "no";
    if (noDeco) {
      return keys.filter((k) => !/^DECORATION$/i.test(String(k).trim()));
    }
    return keys;
  }, [addonMetaData]);
  const [addOnCount, setAddOnCount] = useState(0);
  const [fallbackLoading, setFallbackLoading] = useState(() =>
    Boolean(draft?.slotId && !hasNonemptyAddonCategories(addonMetaData))
  );
  const currentAddon = useMemo(() => {
    if (!totalAddons.length) return [];
    return addonMetaData.addonsData[totalAddons[addOnCount]] || [];
  }, [addonMetaData, totalAddons, addOnCount]);
  const [selectedAddon, setSelectedAddon] = useState([]);
  const [addonCost, setAddOnCost] = useState(0);
  const [nameOnCake, setNameOnCake] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isCustomize, setIsCustomize] = useState([]);
  const message = "Please Enter Required Details.";

  useEffect(() => {
    if (hasNonemptyAddonCategories(addonMetaData)) {
      setFallbackLoading(false);
      return;
    }
    const d = loadBookingFlow();
    if (!d?.slotId) {
      setFallbackLoading(false);
      return;
    }
    if (fallbackFetchedRef.current) {
      setFallbackLoading(false);
      return;
    }
    fallbackFetchedRef.current = true;
    setFallbackLoading(true);
    const controller = new AbortController();
    axios
      .get(Constants.GET_META_DATA_ADDONS, { signal: controller.signal })
      .then((res) => {
        const meta = normalizeAddonMetaPayload(res?.data ?? {});
        setAddonMetaData(meta);
        persistAddonMeta(meta);
      })
      .catch(() => {})
      .finally(() => setFallbackLoading(false));
    return () => controller.abort();
  }, [addonMetaData, setAddonMetaData]);

  useEffect(() => {
    if (!totalAddons.length) return;
    if (addOnCount >= totalAddons.length) {
      setAddOnCount(0);
    }
  }, [totalAddons, addOnCount]);

  useEffect(() => {
    const draft = loadBookingFlow();
    const adminPaths = getAdminBookingPaths();
    if (!draft?.slotId) {
      navigate(adminPaths ? adminPaths.form : "/booknow/booking-form", { replace: true });
    } else if (adminPaths && !draft?.adminBooking) {
      navigate(adminPaths.form, { replace: true });
    }
  }, [navigate]);

  const nextAddOn = () => {
    if (isCustomize?.isCustomize === "Y" && !nameOnCake) {
      setErrorMessage(message);
      return;
    }
    if (totalAddons.length - 1 > addOnCount) {
      setAddOnCount(addOnCount + 1);
    } else {
      const draft = loadBookingFlow() || {};
      const adminPaths = getAdminBookingPaths();
      saveBookingFlow({
        ...draft,
        selectedAddons: addonsArrayToMap(selectedAddon),
        nameOnCake,
        reviewTotalCost: baseTheatreCost + addonCost,
      });
      try {
        if (adminPaths) {
          sessionStorage.setItem("pb_review_back_path", adminPaths.addons);
        } else {
          sessionStorage.removeItem("pb_review_back_path");
        }
      } catch (_) {
        /* ignore */
      }
      navigate(adminPaths ? adminPaths.review : "/booknow/review");
    }
  };

  const prevAddon = () => {
    if (addOnCount <= 0) return;
    setAddOnCount(addOnCount - 1);
  };

  const onAddonSelect = (addProps, isSelected) => {
    if (isSelected) {
      setSelectedAddon(selectedAddon.filter((key) => key.id !== addProps.id));
    } else if (
      addProps?.allowMultiple === "Y" &&
      selectedAddon.length > 0
    ) {
      setSelectedAddon((oldArray) => [...oldArray, addProps]);
    } else {
      setSelectedAddon([addProps]);
    }
  };

  useEffect(() => {
    if (!selectedAddon?.length) {
      setAddOnCost(0);
      return;
    }
    let price = 0;
    selectedAddon.forEach((item) => {
      price += Number(item?.price) || 0;
    });
    setAddOnCost(price);
  }, [selectedAddon]);

  useEffect(() => {
    if (nameOnCake) setErrorMessage("");
  }, [nameOnCake]);

  useEffect(() => {
    const isSelected = selectedAddon?.filter(
      (val) => val?.categoryType === totalAddons[addOnCount]
    )[0];
    setIsCustomize(isSelected);
    document.getElementById("customeMessage")?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [selectedAddon, totalAddons, addOnCount]);

  const selectCategory = (index) => {
    setAddOnCount(index);
  };

  if (fallbackLoading) {
    return (
      <section className="party-boss-page w-full bg-[#05020a] pt-10 text-white">
        <div className="mx-auto max-w-lg px-4 text-center text-[#c9c1d9]">
          <p className="text-lg font-semibold">Loading add-ons…</p>
          <p className="mt-2 text-sm text-white/60">
            Please wait while we load optional extras for your booking.
          </p>
        </div>
      </section>
    );
  }

  if (!totalAddons.length) {
    return (
      <section className="party-boss-page w-full bg-[#05020a] pt-10 text-white">
        <div className="mx-auto max-w-lg px-4 text-center text-[#c9c1d9]">
          <p>No add-ons are configured. Continue to review.</p>
          <Button
            variant="light"
            className="mt-3"
            onClick={() => {
              const draft = loadBookingFlow() || {};
              saveBookingFlow({
                ...draft,
                selectedAddons: {},
                nameOnCake: "",
                reviewTotalCost: baseTheatreCost,
              });
              try {
                sessionStorage.removeItem("pb_review_back_path");
              } catch (_) {
                /* ignore */
              }
              const adminPaths = getAdminBookingPaths();
              try {
                if (adminPaths) {
                  sessionStorage.setItem("pb_review_back_path", adminPaths.addons);
                }
              } catch (_) {
                /* ignore */
              }
              navigate(adminPaths ? adminPaths.review : "/booknow/review");
            }}
          >
            Skip to review
          </Button>
        </div>
      </section>
    );
  }

  const currentLabel = totalAddons[addOnCount];

  return (
    <section className="party-boss-page w-full bg-[#05020a] pb-16 pt-8 text-white md:pt-10">
      <div className="party-boss-bg relative z-10 px-3 sm:px-4 md:px-6">
        <div className="mx-auto mb-6 flex max-w-6xl flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[#ffd166]">
              {adminPaths ? "Admin slot booking — add-ons" : "Add-ons"}
            </p>
            {adminPaths ? (
              <p className="mt-1 text-xs text-[#94a3b8]">
                Select extras for the customer, then continue to review and payment.
              </p>
            ) : null}
            <h1 className="text-2xl font-black text-white md:text-3xl">
              Choose add-ons
            </h1>
          </div>
          <div className="rounded-2xl border border-[#ffd166]/30 bg-[#ffd166]/10 px-4 py-2 text-lg font-black text-[#ffd166]">
            Total{" "}
            <i className="bi bi-currency-rupee" aria-hidden />
            {baseTheatreCost + addonCost}
          </div>
        </div>

        <div className="mx-auto flex max-w-6xl flex-col gap-6 lg:flex-row">
          <aside className="w-full shrink-0 rounded-2xl border border-white/20 bg-[#0a0614]/95 p-3 shadow-[0_8px_32px_-12px_rgba(0,0,0,0.45)] ring-1 ring-white/10 lg:w-56">
            <p className="mb-2 px-2 text-xs font-bold uppercase tracking-wider text-[#c9c1d9]">
              Categories
            </p>
            <nav className="flex flex-row flex-wrap gap-1.5 lg:flex-col">
              {totalAddons.map((key, i) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => selectCategory(i)}
                  className={`rounded-xl px-3 py-2.5 text-left text-sm font-bold transition-all duration-200 lg:w-full ${
                    i === addOnCount
                      ? "bg-gradient-to-r from-pink-500 to-violet-600 text-white shadow-lg shadow-pink-500/25 ring-1 ring-white/20"
                      : "border border-white/10 bg-white/5 text-white/80 shadow-sm hover:border-violet-400/30 hover:bg-white/10"
                  }`}
                >
                  {key}
                </button>
              ))}
            </nav>
          </aside>

          <main className="min-w-0 flex-1 rounded-2xl border border-slate-200/80 bg-white p-4 text-[#06132b] shadow-[0_12px_48px_-16px_rgba(22,0,90,0.15)] ring-1 ring-slate-100 md:p-6">
            <h2 className="mb-4 border-b border-slate-200 pb-2 text-xl font-black text-[#16005a]">
              {currentLabel}
            </h2>
            <div className="mb-6 flex flex-wrap justify-center gap-4 sm:gap-5 md:gap-6">
              {currentAddon?.map((propsList) => {
                let isSelected = false;
                if (selectedAddon.length) {
                  isSelected = selectedAddon?.filter(
                    (val) => val?.id === propsList?.id
                  )[0];
                }
                return (
                  <div
                    key={propsList.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onAddonSelect(propsList, isSelected)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        onAddonSelect(propsList, isSelected);
                    }}
                    className={
                      isSelected
                        ? "addon-list selected-addon group flex w-[10.5rem] cursor-pointer flex-col items-stretch rounded-2xl border-2 border-transparent bg-gradient-to-b from-pink-50 via-white to-violet-50 p-3 text-center shadow-[0_12px_40px_-12px_rgba(192,38,211,0.45)] ring-2 ring-pink-500/90 ring-offset-2 ring-offset-white transition duration-200 sm:w-[11.5rem] sm:p-4 md:w-[12rem]"
                        : "addon-list group flex w-[10.5rem] cursor-pointer flex-col items-stretch rounded-2xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/95 p-3 text-center shadow-[0_6px_24px_-8px_rgba(22,0,90,0.12)] transition duration-200 hover:-translate-y-1 hover:border-violet-300/70 hover:shadow-[0_14px_40px_-12px_rgba(109,40,217,0.2)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-500 sm:w-[11.5rem] sm:p-4 md:w-[12rem]"
                    }
                  >
                    <div
                      className={
                        isSelected
                          ? "mb-2 flex h-40 w-full items-center justify-center rounded-xl bg-white/80 p-3 ring-1 ring-pink-200/60 sm:h-44 md:h-48"
                          : "mb-2 flex h-40 w-full items-center justify-center rounded-xl bg-slate-50/90 p-3 ring-1 ring-slate-200/80 transition group-hover:bg-white group-hover:ring-violet-200/60 sm:h-44 md:h-48"
                      }
                    >
                      <AddonItemImage
                        imageName={propsList.imageName}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <span className="logo-color-1 mb-2 min-h-[2.5rem] px-0.5 text-sm font-semibold leading-snug text-[#16005a]">
                      {propsList.itemName}
                    </span>
                    <span className="mt-auto rounded-lg bg-[#16005a]/[0.06] py-1.5 text-base font-black text-[#16005a]">
                      <i className="bi bi-currency-rupee text-[0.85em]" />
                      {propsList.price}
                    </span>
                  </div>
                );
              })}
            </div>

            {isCustomize?.isCustomize === "Y" && (
              <div className="mb-6 flex justify-center" id="customeMessage">
                <div className="w-full max-w-md">
                  {errorMessage && (
                    <div className="mb-2 text-center text-sm text-red-600">
                      {errorMessage}
                    </div>
                  )}
                  <input
                    type="text"
                    className="form-control mx-auto w-full rounded-xl border-2 border-slate-200 px-4 py-2 font-semibold"
                    placeholder="Enter name on cake / person"
                    maxLength={25}
                    value={nameOnCake}
                    onChange={(e) => setNameOnCake(e.target.value)}
                  />
                </div>
              </div>
            )}

            <p className="mb-4 text-sm text-red-700 md:text-base">
              {currentLabel === "DECORATION"
                ? "Note: Decorations are not customizable. Please select predefined add-ons."
                : ""}
              {currentLabel === "CAKES"
                ? "Note: Images are for demonstration only. Actual cake may differ."
                : ""}
              {currentLabel === "PHOTOS" ? `Note: ${PHOTOS}` : ""}
              {currentLabel === "PARTY PROPS" ? `Note: ${PHOTOPROPS}` : ""}
            </p>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-between">
              <button
                type="button"
                className="rounded-2xl border border-slate-300 bg-white px-4 py-3 font-bold text-slate-800"
                onClick={() => {
                  const adminPaths = getAdminBookingPaths();
                  navigate(adminPaths ? adminPaths.form : "/booknow/booking-form");
                }}
              >
                Back to form
              </button>
              <div className="flex gap-2">
                {addOnCount > 0 && (
                  <button
                    type="button"
                    className="rounded-2xl border border-slate-300 bg-slate-100 px-4 py-3 font-bold text-slate-800"
                    onClick={prevAddon}
                  >
                    Previous category
                  </button>
                )}
                <button
                  type="button"
                  className="rounded-2xl bg-gradient-to-r from-pink-500 to-violet-600 px-6 py-3 font-black text-white shadow-lg"
                  onClick={nextAddOn}
                >
                  {totalAddons.length - 1 > addOnCount
                    ? "Next category"
                    : "Review & pay"}
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </section>
  );
}
